import { supabase } from '../config/supabase';
import { formatCurrency } from '../utils/currency';
import { inventoryService } from './inventoryService';
import { contactsService } from './contactsService';
import { paymentsService } from './paymentsService';
import { Contact } from './contactsService';

// Utility functions for atomic operations
const atomicUtils = {
  // Check if error is network-related
  isNetworkError: (error: any): boolean => {
    return error?.message?.includes('fetch') ||
           error?.message?.includes('network') ||
           error?.code === 'NETWORK_ERROR' ||
           !navigator.onLine;
  },

  // Retry function with exponential backoff
  retryWithBackoff: async <T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> => {
    let lastError: Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;

        if (attempt === maxRetries || !atomicUtils.isNetworkError(error)) {
          throw error;
        }

        const delay = baseDelay * Math.pow(2, attempt);
        console.log(`Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  },

  // Enhanced error logging
  logTransactionError: (operation: string, error: Error, transactionLog: string[]) => {
    const errorDetails = {
      operation,
      message: error.message,
      timestamp: new Date().toISOString(),
      isNetworkError: atomicUtils.isNetworkError(error),
      transactionLog: [...transactionLog]
    };

    console.error('Atomic transaction failed:', errorDetails);

    // In a production app, you might want to send this to an error reporting service
    // errorReportingService.captureException(error, { extra: errorDetails });
  },

  // Transaction logger for debugging
  createTransactionLogger: (operation: string) => {
    const logs: string[] = [];
    const startTime = Date.now();

    return {
      log: (message: string) => {
        const timestamp = new Date().toISOString();
        const logEntry = `[${timestamp}] ${message}`;
        logs.push(logEntry);
        console.log(`${operation}:`, message);
      },

      getLogs: () => [...logs],

      getDuration: () => Date.now() - startTime,

      logError: (error: Error) => {
        atomicUtils.logTransactionError(operation, error, logs);
      }
    };
  }
};

export interface PurchaseOrder {
  id: string;
  business_id: string;
  po_number: string;
  vendor_id: string;
  vendor?: Contact;
  purchase_date: string;
  description?: string;
  status: 'unpaid' | 'paid'; // Changed from draft/ordered/received/cancelled
  total_amount: number;
  discount_amount: number;
  attachment_urls?: string[];
  created_at: string;
  updated_at: string;
}

export interface PurchaseOrderItem {
  id: string;
  po_id: string;
  item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  discount: number; // Discount per item
  total_price: number;
  created_at: string;
}

export interface PurchaseOrderFormData {
  po_number: string;
  vendor_id: string;
  purchase_date: string;
  description?: string;
  status?: 'unpaid' | 'paid'; // Optional since users can't change it
  discount_amount: number;
  attachment_urls?: string[];
  items: PurchaseOrderItemFormData[];
}

export interface PurchaseOrderItemFormData {
  item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  discount: number;
  total_price: number;
}

// Type for purchase order with items
export interface PurchaseOrderWithItems extends PurchaseOrder {
  items: (PurchaseOrderItem & { item_name: string })[];
}

export const purchaseOrdersService = {
  // Generate purchase order number
  generatePurchaseOrderNumber(): string {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, ''); // HHMMSS
    return `PO-${dateStr}-${timeStr}`;
  },

  // Get all purchase orders for the business
  async getPurchaseOrders(businessId?: string): Promise<{ orders: PurchaseOrder[]; error: Error | null }> {
    try {
      let query = supabase
        .from('purchase_orders')
        .select(`
          *,
          vendor:contacts (*)
        `)
        .order('created_at', { ascending: false });

      // Filter by business_id if provided
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { orders: data || [], error: null };
    } catch (error) {
      return { orders: [], error: error as Error };
    }
  },

  // Get purchase order by ID with items
  async getPurchaseOrderById(id: string, businessId?: string): Promise<{ order: PurchaseOrderWithItems | null; error: Error | null }> {
    try {
      let query = supabase
        .from('purchase_orders')
        .select(`
          *,
          vendor:contacts (*)
        `)
        .eq('id', id);

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data: orderData, error: orderError } = await query.single();

      if (orderError) throw orderError;
      if (!orderData) throw new Error('Purchase order not found');

      // Get the items for this purchase order
      const { data: itemsData, error: itemsError } = await supabase
        .from('purchase_order_items')
        .select(`
          id,
          po_id,
          item_id,
          quantity,
          unit_price,
          discount,
          total_price,
          created_at,
          item:inventory_items (name, sku)
        `)
        .eq('po_id', id);

      if (itemsError) throw itemsError;

      // Combine order and items
      const orderWithItems = {
        ...orderData,
        items: itemsData?.map(item => ({
          id: item.id,
          po_id: item.po_id,
          item_id: item.item_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount || 0,
          total_price: item.total_price,
          created_at: item.created_at,
          item_name: (item.item as any)?.name || ''
        })) || []
      };

      return { order: orderWithItems, error: null };
    } catch (error) {
      return { order: null, error: error as Error };
    }
  },

  // Create a new purchase order with atomic operations
  async createPurchaseOrderAtomic(orderData: PurchaseOrderFormData, businessId?: string): Promise<{ order: PurchaseOrder | null; error: Error | null }> {
    const logger = atomicUtils.createTransactionLogger('createPurchaseOrder');
    const rollbackActions: (() => Promise<void>)[] = [];

    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      logger.log(`Starting atomic purchase order creation for ${orderData.po_number}`);

      // Calculate total amount (sum of item totals minus discount)
      const subtotal = orderData.items.reduce((sum, item) => sum + item.total_price, 0);
      const totalAmount = subtotal - orderData.discount_amount;

      // Step 1: Create the purchase order record
      logger.log('Step 1: Creating purchase order record');
      const { data: order, error: orderError } = await atomicUtils.retryWithBackoff(async () => {
        const result = await supabase
          .from('purchase_orders')
          .insert([
            {
              business_id: businessId,
              po_number: orderData.po_number,
              vendor_id: orderData.vendor_id,
              purchase_date: orderData.purchase_date,
              description: orderData.description,
              status: orderData.status || 'unpaid',
              total_amount: totalAmount,
              discount_amount: orderData.discount_amount,
              attachment_urls: orderData.attachment_urls,
            },
          ])
          .select()
          .single();

        if (result.error) throw result.error;
        if (!result.data) throw new Error('Failed to create purchase order - no data returned');
        return result;
      });

      if (orderError) throw orderError;
      if (!order) throw new Error('Failed to create purchase order - no data returned');

      // Add rollback action for purchase order
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Deleting purchase order record');
        await supabase.from('purchase_orders').delete().eq('id', order.id);
      });

      // Step 2: Create the purchase order items
      logger.log('Step 2: Creating purchase order items');
      if (orderData.items.length > 0) {
        const itemsToInsert = orderData.items.map(item => ({
          po_id: order.id,
          item_id: item.item_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount || 0,
          total_price: item.total_price,
        }));

        const { error: itemsError } = await supabase
          .from('purchase_order_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;

        // Add rollback action for items
        rollbackActions.push(async () => {
          logger.log('ROLLBACK: Deleting purchase order items');
          await supabase.from('purchase_order_items').delete().eq('po_id', order.id);
        });
      }

      // Step 3: Update inventory for each item
      logger.log('Step 3: Updating inventory quantities and costs');
      for (const item of orderData.items) {
        const { item: updatedItem, error: inventoryError } = await inventoryService.updateInventoryFromPurchase(
          item.item_id,
          item.quantity,
          item.unit_price,
          'purchase_order',
          order.id
        );

        if (inventoryError) throw inventoryError;

        // Add rollback action for inventory
        rollbackActions.push(async () => {
          logger.log(`ROLLBACK: Reverting inventory for item ${item.item_id}`);
          // Reverse the purchase by subtracting the quantity
          await inventoryService.updateInventoryFromPurchase(
            item.item_id,
            -item.quantity, // Negative quantity to reverse
            item.unit_price,
            'purchase_order',
            order.id
          );
        });
      }

      // Step 4: Update vendor balance
      logger.log('Step 4: Updating vendor balance');
      if (orderData.vendor_id && totalAmount > 0) {
        const { contact: vendor, error: vendorError } = await contactsService.getContactById(orderData.vendor_id, businessId);

        if (vendorError) throw vendorError;
        if (!vendor) throw new Error('Vendor not found');

        const newBalance = vendor.balance + totalAmount;
        const { error: balanceError } = await contactsService.updateContactBalance(orderData.vendor_id, newBalance);

        if (balanceError) throw balanceError;

        // Add rollback action for vendor balance
        rollbackActions.push(async () => {
          logger.log('ROLLBACK: Reverting vendor balance');
          await contactsService.updateContactBalance(orderData.vendor_id, vendor.balance);
        });
      }

      logger.log('Transaction completed successfully');
      return { order, error: null };

    } catch (error) {
      const err = error as Error;
      logger.log(`Transaction failed: ${err.message}`);

      // Log detailed error information
      logger.logError(err);

      // Execute rollback actions in reverse order
      logger.log('Starting rollback process...');
      for (let i = rollbackActions.length - 1; i >= 0; i--) {
        try {
          await rollbackActions[i]();
        } catch (rollbackError) {
          const rbErr = rollbackError as Error;
          logger.log(`Rollback step ${i} failed: ${rbErr.message}`);
          console.warn(`Rollback step ${i} failed:`, rbErr);
        }
      }

      logger.log('Rollback completed');

      // Return user-friendly error message
      const isNetworkError = atomicUtils.isNetworkError(err);
      const userMessage = isNetworkError
        ? 'Network connection lost. The operation has been rolled back. Please check your connection and try again.'
        : 'Failed to create purchase order. All changes have been rolled back. Please try again.';

      return { order: null, error: new Error(userMessage) };
    }
  },

  // Create a new purchase order (legacy method - now uses atomic version)
  async createPurchaseOrder(orderData: PurchaseOrderFormData, businessId?: string): Promise<{ order: PurchaseOrder | null; error: Error | null }> {
    return this.createPurchaseOrderAtomic(orderData, businessId);
  },

  // Update an existing purchase order
  async updatePurchaseOrder(id: string, orderData: PurchaseOrderFormData, businessId?: string): Promise<{ order: PurchaseOrder | null; error: Error | null }> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      // Calculate total amount
      const subtotal = orderData.items.reduce((sum, item) => sum + item.total_price, 0);
      const totalAmount = subtotal - orderData.discount_amount;

      const updateData: any = {
        po_number: orderData.po_number,
        vendor_id: orderData.vendor_id,
        purchase_date: orderData.purchase_date,
        description: orderData.description,
        total_amount: totalAmount,
        discount_amount: orderData.discount_amount,
        attachment_urls: orderData.attachment_urls,
      };

      // Only include status if it's provided
      if (orderData.status) {
        updateData.status = orderData.status;
      }

      const { data: order, error: orderError } = await supabase
        .from('purchase_orders')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (orderError) throw orderError;

      // Delete existing items
      const { error: deleteError } = await supabase
        .from('purchase_order_items')
        .delete()
        .eq('po_id', id);

      if (deleteError) throw deleteError;

      // Create the new purchase order items
      if (orderData.items.length > 0) {
        const itemsToInsert = orderData.items.map(item => ({
          po_id: id,
          item_id: item.item_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount || 0,
          total_price: item.total_price,
        }));

        const { error: itemsError } = await supabase
          .from('purchase_order_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      // Note: Inventory adjustments and vendor balance updates are now handled by database triggers
      // The trigger will automatically process these when the purchase order is updated

      return { order, error: null };
    } catch (error) {
      return { order: null, error: error as Error };
    }
  },

  // Delete a purchase order with atomic operations
  async deletePurchaseOrderAtomic(id: string, businessId?: string): Promise<{ error: Error | null }> {
    const logger = atomicUtils.createTransactionLogger('deletePurchaseOrder');
    const rollbackActions: (() => Promise<void>)[] = [];

    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      logger.log(`Starting atomic purchase order deletion for ID: ${id}`);

      // Step 1: Get the purchase order data before deletion
      logger.log('Step 1: Fetching purchase order data');
      const { order, error: fetchError } = await this.getPurchaseOrderById(id, businessId);

      if (fetchError) throw fetchError;
      if (!order) throw new Error('Purchase order not found');

      // Step 2: Reverse inventory adjustments for each item
      logger.log('Step 2: Reversing inventory adjustments');
      for (const item of order.items) {
        const { item: updatedItem, error: inventoryError } = await inventoryService.updateInventoryFromPurchase(
          item.item_id,
          -item.quantity, // Negative quantity to reverse the purchase
          item.unit_price,
          'purchase_order',
          id
        );

        if (inventoryError) throw inventoryError;

        // Add rollback action for inventory (restore the original state)
        rollbackActions.push(async () => {
          logger.log(`ROLLBACK: Restoring inventory for item ${item.item_id}`);
          await inventoryService.updateInventoryFromPurchase(
            item.item_id,
            item.quantity, // Positive quantity to restore
            item.unit_price,
            'purchase_order',
            id
          );
        });
      }

      // Step 3: Reverse vendor balance
      logger.log('Step 3: Reversing vendor balance');
      if (order.vendor_id && order.total_amount > 0) {
        const { contact: vendor, error: vendorError } = await contactsService.getContactById(order.vendor_id, businessId);

        if (vendorError) throw vendorError;
        if (!vendor) throw new Error('Vendor not found');

        const newBalance = vendor.balance - order.total_amount;
        const { error: balanceError } = await contactsService.updateContactBalance(order.vendor_id, newBalance);

        if (balanceError) throw balanceError;

        // Add rollback action for vendor balance (restore original balance)
        rollbackActions.push(async () => {
          logger.log('ROLLBACK: Restoring vendor balance');
          await contactsService.updateContactBalance(order.vendor_id, vendor.balance);
        });
      }

      // Step 4: Delete inventory transactions
      logger.log('Step 4: Deleting inventory transactions');
      const { error: transactionDeleteError } = await supabase
        .from('inventory_transactions')
        .delete()
        .eq('reference_type', 'purchase_order')
        .eq('reference_id', id);

      if (transactionDeleteError) throw transactionDeleteError;

      // Add rollback action for transactions (this would be complex to restore, so we'll log it)
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Note - inventory transactions were deleted and cannot be automatically restored');
      });

      // Step 5: Delete purchase order items
      logger.log('Step 5: Deleting purchase order items');
      const { error: itemsError } = await supabase
        .from('purchase_order_items')
        .delete()
        .eq('po_id', id);

      if (itemsError) throw itemsError;

      // Add rollback action for items
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Restoring purchase order items');
        const itemsToRestore = order.items.map(item => ({
          po_id: id,
          item_id: item.item_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount,
          total_price: item.total_price,
        }));
        await supabase.from('purchase_order_items').insert(itemsToRestore);
      });

      // Step 6: Delete the purchase order
      logger.log('Step 6: Deleting purchase order record');
      const { error: deleteError } = await supabase
        .from('purchase_orders')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Add rollback action for purchase order
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Restoring purchase order record');
        await supabase.from('purchase_orders').insert([{
          id: order.id,
          business_id: order.business_id,
          po_number: order.po_number,
          vendor_id: order.vendor_id,
          purchase_date: order.purchase_date,
          description: order.description,
          status: order.status,
          total_amount: order.total_amount,
          discount_amount: order.discount_amount,
          attachment_urls: order.attachment_urls,
        }]);
      });

      logger.log('Transaction completed successfully');
      return { error: null };

    } catch (error) {
      const err = error as Error;
      logger.log(`Transaction failed: ${err.message}`);

      // Log detailed error information
      logger.logError(err);

      // Execute rollback actions in reverse order
      logger.log('Starting rollback process...');
      for (let i = rollbackActions.length - 1; i >= 0; i--) {
        try {
          await rollbackActions[i]();
        } catch (rollbackError) {
          const rbErr = rollbackError as Error;
          logger.log(`Rollback step ${i} failed: ${rbErr.message}`);
          console.warn(`Rollback step ${i} failed:`, rbErr);
        }
      }

      logger.log('Rollback completed');

      // Return user-friendly error message
      const isNetworkError = atomicUtils.isNetworkError(err);
      const userMessage = isNetworkError
        ? 'Network connection lost. The operation has been rolled back. Please check your connection and try again.'
        : 'Failed to delete purchase order. All changes have been rolled back. Please try again.';

      return { error: new Error(userMessage) };
    }
  },

  // Delete a purchase order (legacy method - now uses atomic version)
  async deletePurchaseOrder(id: string, businessId?: string): Promise<{ error: Error | null }> {
    return this.deletePurchaseOrderAtomic(id, businessId);
  },

  // Get purchase order formatted total
  getFormattedTotal(amount: number): string {
    return formatCurrency(amount);
  },

  // Get purchase order status label
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      unpaid: 'Unpaid',
      paid: 'Paid',
    };
    return labels[status] || status;
  },

  // Get purchase order status badge class
  getStatusBadgeClass(status: string): string {
    const classes: Record<string, string> = {
      unpaid: 'badge-warning',
      paid: 'badge-success',
    };
    return classes[status] || 'badge';
  },

  // Update purchase order status
  async updatePurchaseOrderStatus(id: string, status: 'unpaid' | 'paid', businessId?: string): Promise<{ error: Error | null }> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      const { error } = await supabase
        .from('purchase_orders')
        .update({ status })
        .eq('id', id)
        .eq('business_id', businessId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Get payment status from payments
  async getPaymentStatus(poId: string, businessId?: string): Promise<'unpaid' | 'paid'> {
    try {
      // Get total payments for this purchase order
      const { total, error } = await paymentsService.getTotalPaymentsForPurchaseOrder(poId, businessId);

      if (error) {
        console.error('Error getting payment total:', error);
        return 'unpaid';
      }

      // Get purchase order total
      const { data: order, error: orderError } = await supabase
        .from('purchase_orders')
        .select('total_amount')
        .eq('id', poId)
        .eq('business_id', businessId || '')
        .single();

      if (orderError || !order) {
        console.error('Error getting purchase order:', orderError);
        return 'unpaid';
      }

      // If total paid >= total amount, status is 'paid'
      return total >= order.total_amount ? 'paid' : 'unpaid';
    } catch (error) {
      console.error('Error determining payment status:', error);
      return 'unpaid';
    }
  },




};